import type { FunnelStepConfig, ListingProviderDisplay } from '../../types/listing-funnel';
import { FunnelStep } from '../../types/listing-funnel';

// ============================================================
// Route path — single constant so route naming is easy to change
// ============================================================
export const FUNNEL_BASE_PATH = '/listing-video';

// ============================================================
// Copy / Messaging
// ============================================================

export const HERO_HEADLINE = 'Turn Any Property Listing Into a Video in Seconds';
export const HERO_SUBHEADLINE =
  'Paste a listing URL or upload up to 5 property images and ImageMotion will create a short AI preview video. No account needed.';
export const INPUT_PLACEHOLDER = 'https://www.rightmove.co.uk/properties/...';
export const CTA_PRIMARY = 'Create My Preview Video';
export const CTA_UPLOAD = 'Upload & Create Preview';
export const SUPPORT_TEXT = 'URL import works with Rightmove and OnTheMarket. Upload images from any source.';

export const PREVIEW_NOTE =
  'Preview generated from your listing images.';
export const SIGNUP_CTA =
  'Create a free account to download it or generate the full version.';
export const DOWNLOAD_CTA = 'Create free account to download';
export const DOWNLOAD_CTA_AUTH = 'Download Preview';
export const FULL_VERSION_CTA = 'Generate full version in ImageMotion';

export const UPSELL_HEADLINE = 'Get the full marketing pack with ReHub';
export const UPSELL_TOOLS = [
  {
    name: 'Photoclear AI',
    description: 'Remove clutter, enhance lighting and perfect property photos instantly using AI.',
    logo: 'https://photoclear.rehub.studio/Photoclear%20Logo%20HR%20Trans.png',
    url: 'https://photoclear.rehub.studio',
  },
  {
    name: '360 Tours Studio',
    description: 'Build immersive virtual tours for property listings and developments.',
    logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png',
    url: 'https://360tours.rehub.studio',
  },
  {
    name: 'Virtual Staging Studio',
    description: 'Transform empty rooms into beautifully furnished interiors in minutes.',
    logo: 'https://staging.rehub.studio/Virtual%20Staging%20Studio%20Logo%20(500%20×%20300%20Px)%20-%202.png',
    url: 'https://staging.rehub.studio',
  },
  {
    name: 'Planos',
    description: 'Create interactive floor plans that allow buyers to explore properties visually.',
    logo: 'https://planos.rehub.studio/Planos%20Logo%20Red.png',
    url: 'https://planos.rehub.studio',
  },
  {
    name: 'Sketchplan',
    description: 'Generate professional floor plans from rough sketches using AI.',
    logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp',
    url: 'https://sketchplan.rehub.studio',
  },
  {
    name: 'BatchTools',
    description: 'Batch resize, watermark and rename property photos. Free utility for estate agents.',
    logo: 'https://tools.rehub.studio/logo.png',
    url: 'https://tools.rehub.studio',
  },
];

// ============================================================
// How it Works
// ============================================================

export const HOW_IT_WORKS_STEPS_UK = [
  {
    number: '1',
    title: 'Paste a URL or upload images',
    description: 'Use a Rightmove or OnTheMarket listing link, or upload up to 5 property photos directly.',
  },
  {
    number: '2',
    title: 'AI creates cinematic motion',
    description: 'ImageMotion transforms still photos into smooth cinematic video clips with AI-generated camera motion.',
  },
  {
    number: '3',
    title: 'Get your preview video',
    description: 'A short branded preview with intro, music and transitions — ready in under 2 minutes.',
  },
];

export const HOW_IT_WORKS_STEPS_GLOBAL = [
  {
    number: '1',
    title: 'Upload your property photos',
    description: 'Select up to 5 of your best property images — exterior, kitchen, living room, bedrooms.',
  },
  {
    number: '2',
    title: 'AI creates cinematic motion',
    description: 'ImageMotion transforms still photos into smooth cinematic video clips with AI-generated camera motion.',
  },
  {
    number: '3',
    title: 'Get your preview video',
    description: 'A short branded preview with intro, music and transitions — ready in under 2 minutes.',
  },
];

// ============================================================
// Supported Providers (URL import only)
// ============================================================

export const SUPPORTED_PROVIDERS: ListingProviderDisplay[] = [
  { id: 'rightmove', name: 'Rightmove', hostname: 'rightmove.co.uk' },
  { id: 'onthemarket', name: 'OnTheMarket', hostname: 'onthemarket.com' },
];

// ============================================================
// Processing Steps
// ============================================================

export const FUNNEL_STEPS: FunnelStepConfig[] = [
  { step: FunnelStep.VALIDATING, label: 'Preparing images', description: 'Getting your property images ready' },
  { step: FunnelStep.EXTRACTING, label: 'Uploading to ImageMotion', description: 'Sending images for processing' },
  { step: FunnelStep.SELECTING, label: 'Queued for generation', description: 'Your project is in the queue' },
  { step: FunnelStep.GENERATING, label: 'Creating preview video', description: 'Generating cinematic motion with AI' },
  { step: FunnelStep.RENDERING, label: 'Rendering your result', description: 'Composing the final preview video' },
];

// ============================================================
// Error Messages
// ============================================================

export const ERROR_MESSAGES: Record<string, { title: string; description: string }> = {
  unsupported_domain: {
    title: 'This listing site isn\'t supported for URL import',
    description: 'URL import currently works with Rightmove and OnTheMarket. For other portals, use the "Upload Images" tab to upload photos directly.',
  },
  extraction_failed: {
    title: 'We couldn\'t extract images from that page',
    description: 'The listing page may have changed or the images may not be accessible. Try the "Upload Images" tab instead.',
  },
  render_failed: {
    title: 'Preview couldn\'t be rendered this time',
    description: 'Something went wrong during video generation. Please try again.',
  },
  rate_limited: {
    title: 'Preview limit reached',
    description: 'You\'ve already generated a preview recently. Create a free account to generate more, or try again later.',
  },
  invalid_url: {
    title: 'Please enter a valid listing URL',
    description: 'Paste a full URL from Rightmove or OnTheMarket.',
  },
  no_images: {
    title: 'Please upload at least one image',
    description: 'Select up to 5 property photos to create your preview video.',
  },
  upload_failed: {
    title: 'Image upload failed',
    description: 'We couldn\'t upload your images. Please try again.',
  },
  region_blocked: {
    title: 'Not available in your region',
    description: 'This free preview service is currently available in the UK, Europe, US, Canada, Australia and UAE.',
  },
  generic: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
  },
};

// ============================================================
// Polling Config
// ============================================================

export const POLL_INTERVAL_MS = 3000;
export const MAX_POLL_ATTEMPTS = 120; // ~6 minutes max

// ============================================================
// Client-side URL Validation (only Rightmove + OnTheMarket)
// ============================================================

const SUPPORTED_HOSTNAMES = [
  'rightmove.co.uk', 'www.rightmove.co.uk',
  'onthemarket.com', 'www.onthemarket.com',
];

export function isValidListingUrl(url: string): { valid: boolean; error?: string } {
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { valid: false, error: 'invalid_url' };
    }
    const hostname = parsed.hostname.replace(/^www\./, '');
    const supported = SUPPORTED_HOSTNAMES.some(
      (h) => h === parsed.hostname || h === hostname
    );
    if (!supported) {
      return { valid: false, error: 'unsupported_domain' };
    }
    return { valid: true };
  } catch {
    return { valid: false, error: 'invalid_url' };
  }
}

export const MAX_UPLOAD_IMAGES = 5;
export const MAX_UPLOAD_FILE_SIZE_MB = 30;
